/**
 * Toast copy for governance decision actions (CT-5095 — Gov Review / Train Me).
 *
 * Errors thrown by the change-request / claim-suggestion services carry:
 *   - `.code` — msgCode (CHANGE_REQUEST_NOT_FOUND, SUGGESTION_NOT_FOUND, REQ001,
 *     MWCS001, SYS001, …) or a client-side guard code (IDS_MISSING,
 *     SUGGESTION_ID_MISSING — the GAP-1 case where no pending suggestionUuid exists).
 *   - `.status` — internal statusCode, or the bare HTTP status (e.g. 500 on the
 *     `approve` → SYS001 backend bug tracked in CT-4867).
 *
 * All copy lives under `governance.*`; callers pass the action for a sensible fallback.
 */

/** msgCode / guard code → locale key prefix under `governance.*`. */
const GOVERNANCE_ERROR_KEYS = {
  CHANGE_REQUEST_NOT_FOUND: 'errorNotFound',
  SUGGESTION_NOT_FOUND: 'errorNotFound',
  REQ001: 'errorValidation',
  MWCS001: 'errorValidation',
  VALIDATION_ERROR: 'errorValidation',
  SYS001: 'errorServer',
  // Client-side guards (no request made):
  IDS_MISSING: 'errorIdsMissing',
  // GAP-1: the pending suggestion's uuid is not exposed by any read endpoint.
  SUGGESTION_ID_MISSING: 'errorSuggestionId',
};

/**
 * @param {import('@aimsos/i18n').TFunction} t
 * @param {Error & { code?: string, status?: number }} [error]
 * @param {'approve'|'reject'|'correct'|'promote'|'decline'|'suggest'} [action]
 * @returns {{ title: string, message: string }}
 */
export function governanceActionErrorToast(t, error, action = 'resolve') {
  const code = error?.code;
  const keyPrefix = code && GOVERNANCE_ERROR_KEYS[code];
  if (keyPrefix) {
    return { title: t(`governance.${keyPrefix}Title`), message: t(`governance.${keyPrefix}Message`) };
  }
  // Already-resolved conflict (state conflict on resolve/accept/decline).
  if (error?.status === 409) {
    return { title: t('governance.errorConflictTitle'), message: t('governance.errorConflictMessage') };
  }
  // Bare / internal server error (e.g. the approve → 500 SYS001 backend bug).
  if (error?.status === 500) {
    return { title: t('governance.errorServerTitle'), message: t('governance.errorServerMessage') };
  }
  return {
    title: t('governance.actionFailedTitle'),
    message: error?.message || t('governance.actionFailedMessage', { action }),
  };
}
